/* eslint-disable @typescript-eslint/no-explicit-any */

export interface AnalyticsContext {
  sku?: string;
  price?: number;
  membership_quantity?: number;
  membership_type?: string;
  [attr: string]:
    | number
    | string
    | boolean
    | null
    | undefined
    | AnalyticsContext
    | PurchaseContext;
}

export interface UserProfile {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface PageViewContext {
  page_location: string;
  language: string;
  page_title: string;
}

export type ImpactCategories = '12-month' | '24-month' | '';

export interface ItemContext {
  item_id: string;
  item_name: string;
  item_variant?: string;
  price: number;
  quantity: number;
  category: ImpactCategories;
}

export interface PurchaseContext {
  currency: string;
  transaction_id: string;
  value: number;
  coupon?: string;
  shipping: number;
  tax: number;
  items: ItemContext[];
  variables?: Record<string, any>; // Pass any type of variable to GTM
}

export type InitializeFunction<T> = (properties: T) => void;
export type EventFunction = (
  eventName: string,
  context?: AnalyticsContext,
) => void;
export type UserEventFunction = (
  eventName: string,
  context?: AnalyticsContext,
  trigger?: { type: string },
) => void;
export type IdentifyFunction = (user: UserProfile) => void;
export type ErrorFunction = (err: any) => void;
export type GetExperimentVariantFunction = (
  name: string,
  experimentFilters?: AnalyticsContext,
) => Promise<string>;
export type PageViewFunction = (context: PageViewContext) => void;
export type PurchaseFunction = (context: PurchaseContext) => void;

export interface AnalyticsSource<T> {
  isInitialized: () => boolean;
  addAction: (name: string) => unknown;
  initialize: InitializeFunction<T>;
  trackEvent?: EventFunction;
  trackError?: ErrorFunction;
  identify?: IdentifyFunction;
  setUserId?: (userId: number) => void;
  setUserProperty?: (key: string, value: string) => void;
  unsetUserProperty?: (key: string) => void;
  getDeviceId?: () => string | null | undefined;
  getSessionId?: () => string | null | undefined;
}

export interface ExperimentServiceVariantServer {
  name: string;
  filters?: AnalyticsContext;
  experimentUUID: string;
}

/**
 * TODO infer types from objects.
 */
export interface ExperimentSource {
  getVariant: GetExperimentVariantFunction;
  getVariantByUserToken: (
    name: string,
    userToken: string,
    experimentFilters?: AnalyticsContext,
  ) => Promise<string | null>;
  getVariantOnServer: (
    props: ExperimentServiceVariantServer,
  ) => Promise<{ variant: string | null }>;
}

export interface GoogleSource<T> {
  initialize: InitializeFunction<T>;
  trackPageView: PageViewFunction;
  trackPurchase: PurchaseFunction;
  trackGoogleEvent: EventFunction;
}

export interface UserEventTrackerProps {
  callbackFn: UserEventFunction;
  trackClicks?: boolean;
  trackScroll?: boolean;
}

export interface RouteChangeTrackerOptions {
  root: Element | Document | null;
  rootMargin: string;
  threshold: number;
}

export interface RouteChangeTrackerProps {
  eventName: string;
  options?: RouteChangeTrackerOptions;
}

export interface WhoopAnalytics<T>
  extends AnalyticsSource<T>,
    ExperimentSource,
    GoogleSource<T> {
  trackAmplitudeEvent: EventFunction;
  setAmplitudeUserId: (userId: number) => void;
  setAmplitudeUserProperty: (key: string, value: string) => void;
  unsetAmplitudeUserProperty: (key: string) => void;
  trackSegmentEvent: EventFunction;
  registerUserEventTracker: InitializeFunction<UserEventTrackerProps>;
  notifyRouteChange: InitializeFunction<RouteChangeTrackerProps>;
  trackDatadogAction: (name: string) => void;
}

export const enum AmplitudeEvents {
  // Page views
  // If adding a page view, also add it to the PAGE_VIEWS array below
  ViewedJoinFlow = 'Viewed Join Flow',
  ViewedAccessoriesScreen = 'Viewed Accessories Screen',
  ViewedOrderScreen = 'Viewed Order Screen',
  ViewedMembershipScreen = 'Viewed Select Membership Screen',
  ViewedFamilyMembershipScreen = 'Viewed Family Membership Screen',
  ViewedReceiptScreen = 'Viewed Receipt Screen',
  ViewedRetryScreen = 'Viewed Retry Screen',
  ViewedPasswordScreen = 'Viewed Join Flow PW Screen',
  ViewedBandSelectionScreen = 'Viewed Band Selection Screen',
  ViewedOptionsScreen = 'Viewed Options Screen',
  ViewedLearnMoreModal = 'Viewed Join Flow Learn More Modal',
  ViewedUpcycleScreen = 'Viewed Upcycle Upgrade Flow',

  // Landing page interactions
  ProductDetailsViewed = 'Product Details Viewed', // Only used in old app, not ecomm
  ClickedFamilyMembership = 'Clicked Family Membership',

  // Cart interactions
  AddedItemToCart = 'Added Item to Cart',
  RemovedItemFromCart = 'Removed Item from Cart',
  ViewedCart = 'Viewed Cart',
  CloseCart = 'Close Cart',

  // Accessories page interactions
  OpenProductDetailsModal = 'Product Details Opened Modal',
  CloseProductDetailsModal = 'Product Details Closed Modal',
  OpenWhoopYourWayModal = 'Open WYW Modal',
  OpenReviewsModal = 'Product Details Clicked Reviews Details',
  ModalBackButton = 'Modal Back Button',

  // Order page interactions
  ExpressCheckout = 'Express Checkout',

  StartedUserSection = 'Started User Section',
  StartedAddressSection = 'Started Address Section',
  StartedPaymentForm = 'Started Payment Form',
  CompletedUserSection = 'Completed User Section',
  CompletedAddressSection = 'Completed Address Section',
  CompletedShippingSection = 'Completed Shipping Section',
  PressedPlaceOrder = 'Pressed Place Order',
  PlacedOrder = 'Placed Order',
  JoinFlowError = 'Join Flow Error',
  OpenPromoCodeInput = 'Expanded Promo Code Section',
  EnteredPromoCode = 'Entered Promo Code',
  EmailSuggestionGiven = 'Email Suggestion Given',
  ClickedEdit = 'Clicked Edit',

  // Receipt page interactions
  RAFInviteFriend = 'Refer Friend- Invite Friend',
  ReferralInvitationSent = 'Referral Invitation Sent',

  // Trial page interactions
  TrialsViewedAccountCreation = 'Trials - Viewed Account Creation',
  TrialsCreatedAccount = 'Trials - Created Account',
  TrialsViewedPaymentPage = 'Trials - Viewed Payment Page',
  TrialsClickedApplePay = 'Trials - Clicked Apple Pay',
  TrialsClickedGooglePay = 'Trials - Clicked Google Pay',
  TrialsClickedCreditCardButton = 'Trials - Clicked Credit Card Button',
  TrialsSubmittedCreditCard = 'Trials - Submitted Credit Card',
  TrialsClickedQrCameraButton = 'Trials - Clicked QR Camera Button',
  TrialsViewedStrapIdConfig = 'Trials - Viewed Strap ID Configuration',
  TrialsClickedActivateBand = 'Trials - Clicked Activate Band',
  TrialsActivatedStrapId = 'Trials - Activated Strap ID',
  TrialsViewedSuccessPage = 'Trials - Viewed Success Page',
  TrialsClickedDownloadApp = 'Trials - Clicked Download App',
  HeroCarousel = 'Join Flow Hero Carousel',

  // Gift Messaging
  GiftMessaging = 'Gift Messaging',

  // RAF Gift
  RAFGiftSignInPage = 'RAF Gift Sign In Page',

  // Other
  Scrolled = 'Scrolled',
  ClickedHeader = 'Clicked Header',
  OpenedFAQ = 'Opened FAQ',

  // Web Vitals
  WebVitals = 'Join Flow Web Vitals',

  // Country Selector
  JoinFlowCountrySelector = 'Join Flow Country Selector',

  // Login
  LoginPage = 'Login Page',
  UpgradeFlowBlocked = 'Upgrade Flow Blocked',
}

export const enum AmplitudeProperties {
  FamilyMembershipVisible = 'Family Membership Visible',
  FamilyCartUpsellClick = 'Cart - Family Membership Upsell',
  FamilyCartChangeSize = 'Cart - Change Family Size',
}

export const AmplitudeActions = {
  Viewed: 'viewed',
  Toggle: 'toggle',
  Landing: 'landing',
  Scrolled: 'scrolled',
  SkipMemSelect: 'skip-membership-select',
  NoOptionOnOPL: 'no-option-opl',
  SoftSelectChange: 'soft-select-change',
  Navigation: 'navigation',
  Continue: 'continue',
  ContinueWithSelection: 'continue-with-selection',
  ContinueWithIncluded: 'continue-with-included-band',

  // Express Checkout
  ExpressActionOpen: 'Open',
  ExpressActionCancel: 'Cancel',
  ExpressActionAddressError: 'Address Error',
  ExpressActionPaymentError: 'Payment Error',
  ExpressActionStripeRefresh: 'Mismatched Stripe Accounts',
  ExpressActionSuccess: 'Success',

  // Country Selector
  CountrySelectorActionOpen: 'Opened Selector',
  CountrySelectorActionClose: 'Closed Selector',
  CountrySelectorActionChangeSite: 'Changed Site',

  // Hero Carousel
  HeroCarouselActionIncrement: 'Clicked Increment',
  HeroCarouselActionDecrement: 'Clicked Decrement',
  HeroCarouselActionHoverScroll: 'Hover Scroll',
  HeroCarouselActionClickedCta: 'Clicked CTA',

  // Family
  ClickMorePlanOptions: 'Click More Plan Options',
  ViewInviteScreen: 'View Invite Screen',
  ViewInviteSentScreen: 'View Invite Sent Screen',

  // Join Flow Errors
  JoinFlowErrorActionCreateOrder: 'create_order',
  JoinFlowErrorActionSetShippingAddress: 'set_shipping_address',
  JoinFlowErrorActionGetReceipt: 'get_receipt_content_post_payment',
  JoinFlowErrorActionPrepareOrder: 'prepare_order_for_payment',
  JoinFlowErrorActionConfirmPayment: 'confirm_payment',
  JoinFlowErrorActionConfirmCardPayment: 'confirm_card_payment',
  JoinFlowErrorActionPlaceOrder: 'place_order',

  // Band Selector
  BandCategoryChange: 'Band Category Change',
  AddedBandToCart: ' Added Band to Cart',

  // Upgrade
  ChangeMembership: 'change membership',
  ChooseUpgradeMethod: 'choose upgrade method',
  ChooseUpcycleMethod: 'choose upcycle method',
  UnclickSwitchToAnnualBilling: 'unclick switch to annual billing',
  ClickSwitchToAnnualBilling: 'click switch to annual billing',
  ViewRemoveUpcycleKitConfirmationModal:
    'view remove upcycle kit confirmation modal',
  ClickRemoveUpcycleKitConfirmationModal:
    'click remove upcycle kit confirmation modal',
  ViewAddUpcycleKitConfirmationModal: 'view add upcycle kit confirmation modal',
  ClickAddUpcycleKitConfirmationModal:
    'click add upcycle kit confirmation modal',
  SignInAttempted: 'sign in attempted',
  SignInError: 'sign in error',
  SignInSuccess: 'sign in success',
  ClickedPasswordReset: 'clicked password reset',
  AlreadyHaveMg: 'already have MG',
  NeedToOnboard: 'need to onboard',
  EnterpriseAccount: 'enterprise account',
  ClickedLookingToDowngradeLink: 'clicked looking to downgrade link',

  // Founding Member
  ViewFoundingMemberConfirmationModal:
    'view founding member confirmation modal',

  // Accessories
  SelectedCategory: 'Selected Category',

  // Compare table
  ChangedMembershipComparison: 'changed membership comparison',

  // OPL feature carousel
  ProductFeatureCarouselScroll: 'product feature carousel scroll',
  ProductFeatureCarouselModalOpen: 'product feature carousel modal open',

  // Advanced whoop labs receipt page callout
  JoinedBloodworkWaitlist: 'Joined Bloodwork Waitlist',
} as const;

export const AmplitudeNavigationTypes = {
  StickyCta: 'StickyCTA',
  MembershipBlock: 'MembershipBlock',
  TrialBlock: 'TrialBlock',
  CompareBlock: 'CompareBlock',
  UpgradeBlock: 'UpgradeBlock',
  SwitchingBlock: 'SwitchingBlock',
  FamilyMembership: 'FamilyMembership',
  MembershipSelect: 'MembershipSelect',
};

export const PAGE_VIEWS = [
  // Join
  AmplitudeEvents.ViewedBandSelectionScreen,
  AmplitudeEvents.ViewedMembershipScreen,
  AmplitudeEvents.ViewedFamilyMembershipScreen,

  // Upgrade
  AmplitudeEvents.ViewedOptionsScreen,
  AmplitudeEvents.ViewedUpcycleScreen,

  // Both
  AmplitudeEvents.ViewedJoinFlow, // bit of a misnomer, used for both flows' OPL pages
  AmplitudeEvents.ViewedAccessoriesScreen,
  AmplitudeEvents.ViewedOrderScreen,
];

export const enum SegmentEvents {
  RAFShareClick = 'Refer Friend- Invite Friend',
}

export enum AmplitudeMembershipType {
  None = 'NONE',
  Trial = '4.0 TRIAL',
  OPLTrial = '5.0 TRIAL',
  One = 'ONE',
  Peak = 'PEAK',
  Life = 'LIFE',
}

export enum UpgradeMembershipOptionsUserProperty {
  OnePeakLife = 'OPL',
  OnePeak = 'OP',
  PeakLife = 'PL',
  Life = 'L',
}

export enum UpcycleMethodUserProperty {
  Gift = 'gift',
  FreeTrial = 'free trial',
}

export enum UpcycleKitOfferedUserProperty {
  Yes = 'yes',
  No = 'no',
}

export enum UpcycleGiftKitAddedUserProperty {
  Yes = 'yes',
  No = 'no',
}

export enum RemoveUpcycleKitConfirmationModalClick {
  RemoveGiftKit = 'remove gift kit',
  BackToGiftOptions = 'back to gift options',
}

export enum AddUpcycleKitConfirmationModalClick {
  AddGiftKit = 'add gift kit',
  DoNotAddGiftKit = 'do not add gift kit',
}
